import { useEffect, useRef, useCallback } from 'react';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { useTerminal, type TerminalStatus } from '../hooks/useTerminal';

interface TerminalProps {
  serial: string;
}

const STATUS_LABELS: Record<TerminalStatus, { text: string; color: string }> = {
  disconnected: { text: 'Disconnected', color: 'text-gray-500' },
  connecting: { text: 'Connecting…', color: 'text-amber-400' },
  waiting: { text: 'Waiting for device…', color: 'text-amber-400' },
  active: { text: 'Active', color: 'text-emerald-400' },
  error: { text: 'Error', color: 'text-red-400' },
};

export function TerminalPanel({ serial }: TerminalProps) {
  const termRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const openedRef = useRef(false);

  const handleData = useCallback((data: string) => {
    // Convert bare \n to \r\n — PTY sends Unix line endings but xterm needs CR+LF
    xtermRef.current?.write(data.replace(/\r?\n/g, '\r\n'));
  }, []);

  const { status, error, open, sendInput, resize, close } = useTerminal({
    serial,
    onData: handleData,
  });

  // Initialize xterm
  useEffect(() => {
    if (!termRef.current) return;

    const xterm = new XTerm({
      theme: {
        background: '#0a0a0f',
        foreground: '#d4d4d8',
        cursor: '#f97316',
        selectionBackground: '#f9731640',
        black: '#18181b',
        red: '#ef4444',
        green: '#22c55e',
        yellow: '#eab308',
        blue: '#3b82f6',
        magenta: '#a855f7',
        cyan: '#06b6d4',
        white: '#d4d4d8',
        brightBlack: '#52525b',
        brightRed: '#f87171',
        brightGreen: '#4ade80',
        brightYellow: '#facc15',
        brightBlue: '#60a5fa',
        brightMagenta: '#c084fc',
        brightCyan: '#22d3ee',
        brightWhite: '#fafafa',
      },
      fontSize: 13,
      fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
      cursorBlink: true,
      cursorStyle: 'block',
      scrollback: 5000,
    });

    const fit = new FitAddon();
    xterm.loadAddon(fit);
    xterm.open(termRef.current);
    fit.fit();

    xtermRef.current = xterm;
    fitRef.current = fit;

    // Forward user input to MQTT — device PTY handles echo
    xterm.onData((data) => {
      sendInput(data);
    });

    // Handle resize
    const observer = new ResizeObserver(() => {
      fit.fit();
    });
    observer.observe(termRef.current);

    xterm.onResize(({ cols, rows }) => {
      resize(cols, rows);
    });

    return () => {
      observer.disconnect();
      xterm.dispose();
      xtermRef.current = null;
      fitRef.current = null;
      openedRef.current = false;
    };
  }, [serial, sendInput, resize]);

  // Open session when MQTT is ready — defer to next frame so container is laid out
  useEffect(() => {
    if (status === 'waiting' && !openedRef.current && xtermRef.current && fitRef.current) {
      openedRef.current = true;
      xtermRef.current.write('\x1b[2m Connecting to device…\x1b[0m\r\n');
      // Wait for layout to settle before reading dimensions
      requestAnimationFrame(() => {
        if (!fitRef.current || !xtermRef.current) return;
        fitRef.current.fit();
        const { cols, rows } = xtermRef.current;
        open(cols, rows);
      });
    }
  }, [status, open]);

  // On active: re-fit and force resize to device so PTY matches viewport
  useEffect(() => {
    if (status === 'active' && xtermRef.current && fitRef.current) {
      fitRef.current.fit();
      const { cols, rows } = xtermRef.current;
      resize(cols, rows);
      xtermRef.current.write('\x1b[32m Connected.\x1b[0m\r\n');
      xtermRef.current.focus();
    } else if (status === 'error' && xtermRef.current && error) {
      xtermRef.current.write(`\x1b[31m ${error}\x1b[0m\r\n`);
    }
  }, [status, error, resize]);

  const statusInfo = STATUS_LABELS[status];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-surface-2 border-b border-surface-3 rounded-t-lg">
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full ${
              status === 'active'
                ? 'bg-emerald-400'
                : status === 'error'
                  ? 'bg-red-400'
                  : status === 'connecting' || status === 'waiting'
                    ? 'bg-amber-400 animate-pulse'
                    : 'bg-gray-600'
            }`}
          />
          <span className={`text-xs font-medium ${statusInfo.color}`}>{statusInfo.text}</span>
          <span className="text-[10px] text-gray-600 font-mono">{serial}</span>
        </div>
        <button
          onClick={close}
          className="text-[10px] text-gray-500 hover:text-red-400 transition-colors"
        >
          End Session
        </button>
      </div>

      {/* Terminal */}
      <div
        ref={termRef}
        onClick={() => xtermRef.current?.focus()}
        className="flex-1 min-h-0 bg-[#0a0a0f] rounded-b-lg overflow-hidden"
        style={{ minHeight: '300px' }}
      />
    </div>
  );
}
